import React, { useState, useEffect } from 'react';
import { Search, Phone, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { menuData, dealsData } from '../data/menuData';

export const Hero = () => {
  const { t, language, getLocalizedContent } = useLanguage();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (value.trim().length > 0) {
      const allItems = [
        ...Object.values(menuData).flat(),
        ...dealsData.map(d => ({ ...d, name: d.title, name_en: d.title_en }))
      ];
      const query = value.toLowerCase().trim();

      const matches = allItems.filter(item => {
        const itemCode = item.code ? item.code.toString().toLowerCase() : '';
        const itemName = item.name ? item.name.toLowerCase() : (item.title ? item.title.toLowerCase() : '');
        const itemNameEn = item.name_en ? item.name_en.toLowerCase() : (item.title_en ? item.title_en.toLowerCase() : '');
        return itemCode.includes(query) || itemName.includes(query) || itemNameEn.includes(query);
      }).slice(0, 5);

      setSuggestions(matches);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (item) => {
    const name = getLocalizedContent(item, 'name');
    setSearchQuery(name);
    navigate(`/search?q=${encodeURIComponent(name)}`);
    setShowSuggestions(false);
  };

  return (
    <div id="hero" className="relative min-h-screen flex items-center justify-center pt-20 pb-16">
      {/* Background & Decorative Elements Wrapper */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/20 to-background" />

        {/* Decorative Elements */}
        <div className="absolute top-20 right-10 w-20 h-20 bg-primary/10 rounded-full blur-xl" />
        <div className="absolute bottom-20 left-10 w-32 h-32 bg-secondary/10 rounded-full blur-xl" />
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-accent/10 rounded-full blur-xl" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Logo */}
        <div className="-mb-5 -mt-7 animate-scale">
          <img
            src="/images/Amir-Pizza-Logo.png"
            alt="Amir's Pizza"
            width="416"
            height="416"
            fetchPriority="high"
            className="w-[20.5rem] h-[20.5rem] sm:w-80 sm:h-80 md:w-96 md:h-96 lg:w-[26rem] lg:h-[26rem] mx-auto object-contain transition-all duration-1030 animate-float"
          />
        </div>

        {/* Title */}
        {/* H1 for SEO - Visually Hidden but semantically present */}
        <h1 className="sr-only">
          Amir's Pizza Heimservice - Pizza, Pasta & Burger in Tittling
        </h1>

        {/* Tagline */}
        <p className="text-xl sm:text-2xl text-muted-foreground mb-8 animate-fade-in">
          {t('tagline')}
        </p>

        {/* Phone Number */}
        <a
          href="tel:08504-9547210"
          className="flex items-center justify-center gap-3 mb-8 animate-slide-up text-3xl sm:text-4xl font-bold text-foreground group transition-colors"
        >
          <Phone className="h-8 w-8 text-primary" />
          <span>{t('phone')}: <span className="group-hover:text-primary transition-colors">08504-9547210</span></span>
        </a>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-12 animate-fade-in relative z-[100]" ref={searchContainerRef}>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t('search')}
              value={searchQuery}
              onChange={handleInputChange}
              onFocus={() => searchQuery.trim().length > 0 && setShowSuggestions(true)}
              className="pl-12 pr-12 py-6 text-lg border-2 border-border focus:border-primary rounded-full transition-all duration-300"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setSuggestions([]);
                  setShowSuggestions(false);
                }}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 p-1 hover:bg-muted rounded-full transition-colors"
                aria-label="Clear search"
              >
                <X className="h-5 w-5 text-muted-foreground hover:text-foreground" />
              </button>
            )}

            {/* Live Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-popover border border-border rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 z-[101]">
                {suggestions.map((item, index) => (
                  <div
                    key={`${item.code || item.name}-${index}`}
                    onClick={() => handleSuggestionClick(item)}
                    className="flex items-center justify-between px-6 py-3 hover:bg-muted cursor-pointer transition-colors border-b border-border/50 last:border-0"
                  >
                    <div className="flex items-center gap-3 text-left">
                      {item.code && (
                        <span className="bg-primary/10 text-primary px-2 py-1 rounded text-xs font-mono font-bold">
                          {item.code}
                        </span>
                      )}
                      <span className="font-medium text-foreground">{getLocalizedContent(item, 'name')}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      <span className={`${((item.price !== undefined && item.price === 0) || (item.prices && ((Object.values(item.prices)[0] || 0) === 0))) ? 'text-green-600 font-bold' : 'text-muted-foreground'}`}>
                        {item.price !== undefined
                          ? (typeof item.price === 'number' ? `${item.price.toFixed(2)}€` : item.price)
                          : (item.prices ? `${language === 'de' ? 'ab ' : 'from '}${(Object.values(item.prices)[0] || 0).toFixed(2)}€` : '')}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </form>

        {/* CTA Button */}
        <Button
          onClick={() => document.getElementById('offers').scrollIntoView({ behavior: 'smooth' })}
          size="lg"
          className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-lg rounded-full shadow-glow animate-scale"
        >
          {t('orderNow')}
        </Button>
      </div>
    </div>
  );
};
